import { Dom, Element as SvgElement, LinkedHTMLElement } from '@svgdotjs/svg.js';
import { v4 as uuidv4 } from 'uuid';

import ElementTools from '@/elements/element/utils/ElementTools';
import TextTools from '@/elements/texts/text/utils/TextTools';

class IllustratorTools {
	static removeUnnecessaryClipPaths(svg: SvgElement) {
		// Buscamos los clip-path del svg, ignoramos los de las imágenes y el principal,
		// ya que estos los creamos
		const defsClipPath: string[] = [];

		svg.find('[clip-path], [style*="clip-path"]:not([style*="clip-path-page"]):not(.main-image)').forEach((element) => {
			const clipPathId = `clipPath${(element.attr('clip-path') || element.css('clip-path'))
				.replace('url("', '')
				.replace('")', '')
				.replace('url(', '')
				.replace(')', '')}`;

			defsClipPath.push(clipPathId);

			element.css('clipPath', '');
			element.attr('clip-path', null);
		});

		if (defsClipPath.length) {
			svg.find(defsClipPath.join(', ')).forEach((element) => {
				element.remove();
			});
		}

		// Eliminamos los gradientTransform
		svg.find('[gradientTransform]').forEach((element) => {
			element.attr('gradientTransform', null);
		});
	}

	static removeGroupTransforms(svg: SvgElement) {
		svg.find('*:not(g)').forEach((elChild) => {
			if (ElementTools.checkIfIsDefsElement(elChild)) return;

			let parent = elChild.parent() as SvgElement;

			// Vamos aplicando los transforms según buscamos el g base que contiene el
			// clip-path container
			while (parent !== null && parent.type !== 'svg') {
				if (elChild.type === 'DIV' || elChild.type === 'metadata') {
					return;
				}
				elChild.transform(parent.transform(), true);

				parent = parent.parent() as SvgElement;
			}

			// Eliminamos los data-* de los hijos
			Object.keys(elChild.node.dataset).forEach((dataKey) => delete elChild.node.dataset[dataKey]);
		});

		// Aplicamos la lógica anterior a los clipPath del elemento
		svg.find('[style*="clip-path"]').forEach((elChild) => {
			if (ElementTools.checkIfIsDefsElement(elChild)) return;

			let parent = elChild.parent() as SvgElement;
			const clipPathId = ElementTools.getIdFromUrl(elChild.css('clip-path').toString());
			const clipPathElement = svg.defs().findOne(clipPathId)?.first() as SvgElement;

			while (parent !== null && parent.type !== 'svg') {
				clipPathElement.transform(parent.transform(), true);

				parent = parent.parent() as SvgElement;
			}
		});

		svg.find('g').forEach((elChild) => {
			// Los transform ya no valen para nada
			elChild.attr('transform', null);
			elChild.attr('id', null);

			const attrs = elChild.attr();

			Object.keys(attrs).forEach((attr: any) => {
				if (attr === 'opacity') {
					const opacity = attrs[attr];

					elChild.find('[style]:not(g)').forEach((el) => {
						// Mergeamos la opacidad
						if (el.attr('style').includes('fill')) {
							el.node.style.fillOpacity = opacity;
						}

						if (el.attr('style').includes('stroke')) {
							el.node.style.strokeOpacity = opacity;
						}

						// Limpiamos los attrs que no necesitamos
						if (!attrs[attr].toString().includes('clip-path')) {
							elChild.attr(attr, null);
						}
					});
				}
			});
		});
	}

	static simplifyTreeOfElements(svg: SvgElement) {
		// Eliminamos los elementos que no sean necesarios
		svg.find('metadata').forEach((element) => {
			element.remove();
		});

		// Quitamos el id al defs
		svg.find('defs').forEach((element) => {
			element.id('');
		});

		// Eliminamos grupos vacíos y <g> innecesarios
		svg
			.find('g')
			.filter((g) => !g.node.hasChildNodes())
			.forEach((g) => g.remove());

		let invalidG = svg.find('g').filter((g) => g.children().length === 1 && !g.node.closest('defs'));

		while (invalidG.length) {
			invalidG.forEach((g) => {
				const firstElementChild = (g.node.firstElementChild as LinkedHTMLElement).instance as SvgElement;
				const parent = g.parent() as Dom;
				const parentPositionInTree = parent.index(g);

				firstElementChild.toParent(parent, parentPositionInTree);
				g.remove();
			});

			invalidG = svg.find('g').filter((g) => g.children().length === 1 && !g.node.closest('defs'));
		}

		// Movemos los textos lo más arriba posible
		svg.find('text').forEach((el) => el.front());

		// Asignamos un data a los elementos para poder trabajar con ellos
		svg.find('*:not(tspan):not(defs)').forEach((el) => {
			if (el.node.closest('defs')) {
				return;
			}

			const dataName = el.type === 'g' ? 'group-link' : 'illustrator-link';

			el.data(dataName, uuidv4());
		});
	}

	static fixTexts(svg: SvgElement) {
		svg.find('[style*="font-family"]').forEach((text) => {
			const fontFamilySplit = text.css('font-family').toString().split(', ');
			const fontFamily = TextTools.getFontByName(
				fontFamilySplit[fontFamilySplit.length - 1]?.replaceAll('"', '') || 'Montserrat'
			);

			text.css('font-family', fontFamily);
		});
	}

	static fixIncompatibleGradients(svg: SvgElement) {
		// Eliminamos los attrs que no soportamos
		svg.find('radialGradient, linearGradient').forEach((gradient) => {
			Object.keys(gradient.attr())
				.filter((attr) => !['id', 'gradientTransform'].includes(attr))
				.forEach((attr) => gradient.attr(attr, null));
		});
	}

	private static getParents(element: SvgElement) {
		const parents: any = [];
		let currentElement: any = element;

		while (currentElement.type !== 'svg') {
			currentElement = currentElement.parent();
			parents.push(currentElement);
		}

		return parents;
	}

	private static getCommonParents(elements: SvgElement[]) {
		const parentList = elements.map((el) => this.getParents(el));

		return parentList.shift().filter((v) => {
			return parentList.every((a) => {
				return a.indexOf(v) !== -1;
			});
		});
	}

	static findBackground(svg: SvgElement) {
		// Pillamos los elementos que no estén en defs
		const bgCandidates = svg
			.find('rect[style*="fill"], path[style*="fill"], polygon[style*="fill"]')
			.filter((el) => !el.node.closest('defs'));
		const parent = this.getCommonParents(bgCandidates)[0];
		let background: any = null;

		// Simulamos recorrer el DOM respetando el orden del árbol y así buscar
		// el elemento que esté abajo del todo
		const loop = (main: SVGElement) => {
			do {
				const isText = main.nodeName === 'text';
				const isImage = main.nodeName === 'image';
				const isPlainText = main.nodeName === '#text';

				if (!background && ['path', 'polygon', 'rect'].includes(main.nodeName)) {
					background = main;
				}

				if (!background && main.hasChildNodes() && !isText && !isPlainText && !isImage) {
					loop(main.firstElementChild as SVGElement);
				}
			} while ((main = main.nextSibling as SVGElement));
		};

		loop(parent.node);

		background.instance.id('illustrator-background');
	}
}

export default IllustratorTools;
